import { Board, BoardFolder, Folder, ItemType, WorkItem, Roadmap } from '@/api';
import { AnalyticsUICharts } from '@/models/data.models';
import {
  AccountRoutes,
  OrganizationPreferencesRoutes,
  RouteNames,
} from '@/router';
import { organizationData } from '@/store/helpers';
import { useRoot } from '@/utils/component';
import { useLRUArray, useNavigateToItem } from '@/utils/helpers';
import { LocalStorageKeys, orgScopedKey } from '@/utils/local-storage';
import { debounce, get, uniqBy } from 'lodash-es';
import { Ref, computed, ref, watch } from 'vue';
import { RouteLocationRaw } from 'vue-router';

export enum SearchPrefix {
  Page = 'page',
  WorkItem = 'item',
  Workstream = 'workstream',
  BoardFolder = 'folder',
  Board = 'board',
  Cycle = 'cycle',
  Roadmap = 'roadmap',
  Recent = 'recent',
}

type PageSearchResult = {
  type: 'page';
  label: string;
  entity: {
    id: string;
    description: string;
    icon: {
      component: 'octicon' | 'fluent-icon' | 'custom-icon';
      name: string;
    };
    /** only handles string and { name, query, params } locations */
    location: RouteLocationRaw;
    isExternal?: boolean;
  };
};
type WorkItemSearchResult = {
  type: 'work-item';
  label: string;
  entity: WorkItem;
};
type WorkstreamSearchResult = {
  type: 'workstream';
  label: string;
  entity: Folder;
};
type BoardFolderSearchResult = {
  type: 'board-folder';
  label: string;
  entity: BoardFolder;
};
type BoardSearchResult = {
  type: 'board';
  label: string;
  entity: Board;
};
type CycleSearchResult = {
  type: 'cycle';
  label: string;
  entity: Board;
};
type RoadmapSearchResult = {
  type: 'roadmap';
  label: string;
  entity: Roadmap;
};
type PrefixSearchResult = {
  type: 'prefix';
  label: string;
  entity: {
    id: SearchPrefix;
    prefix: SearchPrefix;
    description: string;
  };
};

export type MasterSearchResult =
  | PageSearchResult
  | WorkItemSearchResult
  | WorkstreamSearchResult
  | BoardFolderSearchResult
  | BoardSearchResult
  | CycleSearchResult
  | RoadmapSearchResult
  | PrefixSearchResult;

type RecentMasterSearchResult = {
  type: 'recent';
  resultType: MasterSearchResult['type'];
  identifier:
    | PageSearchResult['entity']['id'] // 0
    | WorkItemSearchResult['entity']['number'] // 1
    | WorkstreamSearchResult['entity']['number'] // 1
    | BoardFolderSearchResult['entity']['id'] // 1
    | BoardSearchResult['entity']['id'] // 1
    | CycleSearchResult['entity']['id'] // 1
    | RoadmapSearchResult['entity']['id']; // 1
};

const getAvailablePages = (
  isAdmin = false,
  isDevStrideAdmin = false,
  temp_jiraAdoIntegrationsEnabled = false
): PageSearchResult[] => {
  const ret: PageSearchResult[] = [];

  // Personal

  ret.push({
    type: 'page',
    label: 'My work',
    entity: {
      id: 'my-work',
      description: 'Work assigned, created, or followed by you',
      icon: { component: 'octicon', name: 'person' },
      location: RouteNames.Work,
    },
  });

  ret.push({
    type: 'page',
    label: 'Notifications',
    entity: {
      id: 'notifications',
      description: 'View and manage notifications',
      icon: { component: 'octicon', name: 'bell' },
      location: RouteNames.Notifications,
    },
  });

  // Views

  ret.push({
    type: 'page',
    label: 'Workstreams',
    entity: {
      id: 'workstreams',
      description:
        'View and manage workstreams and items across your organization',
      icon: { component: 'custom-icon', name: 'hierarchy-map-24' },
      location: RouteNames.Map,
    },
  });
  ret.push({
    type: 'page',
    label: 'Boards',
    entity: {
      id: 'boards',
      description: 'Board views for managing work items',
      icon: { component: 'custom-icon', name: 'column-outline' },
      location: RouteNames.Boards,
    },
  });
  ret.push({
    type: 'page',
    label: 'Gantt',
    entity: {
      id: 'gantt',
      description: "Gantt charts for your organization's roadmaps",
      icon: { component: 'fluent-icon', name: 'gantt_chart' },
      location: RouteNames.Gantt,
    },
  });
  ret.push({
    type: 'page',
    label: 'Items',
    entity: {
      id: 'items',
      description: 'View and manage all work items in your organization',
      icon: { component: 'octicon', name: 'note' },
      location: RouteNames.Items,
    },
  });
  ret.push({
    type: 'page',
    label: 'Reports',
    entity: {
      id: 'reports',
      description: 'Analyze and visualize your organization data',
      icon: { component: 'octicon', name: 'graph' },
      location: RouteNames.Analyze,
    },
  });

  ret.push({
    type: 'page',
    label: 'Report: Current progress',
    entity: {
      id: 'report:current-progress',
      description: 'Analyze current progress of work across various dimensions',
      icon: { component: 'custom-icon', name: 'donut' },
      location: {
        name: RouteNames.Analyze,
        query: {
          section: AnalyticsUICharts.CURRENT_PROGRESS,
        },
      },
    },
  });

  ret.push({
    type: 'page',
    label: 'Report: Trending progress',
    entity: {
      id: 'report:trending-progress',
      description:
        'Analyze over-time progress of work across various dimensions',
      icon: { component: 'fluent-icon', name: 'chart_multiple_24' },
      location: {
        name: RouteNames.Analyze,
        query: {
          section: AnalyticsUICharts.TRENDING_PROGRESS,
        },
      },
    },
  });

  ret.push({
    type: 'page',
    label: 'Report: Cycle time',
    entity: {
      id: 'report:cycle-time',
      description:
        'Analyze the time taken to complete work items across various dimensions',
      icon: { component: 'octicon', name: 'stopwatch' },
      location: {
        name: RouteNames.Analyze,
        query: {
          section: AnalyticsUICharts.CUSTOM_CYCLE_TIME,
        },
      },
    },
  });

  ret.push({
    type: 'page',
    label: 'Report: Throughput',
    entity: {
      id: 'report:throughput',
      description:
        'Analyze the rate at which the work is completed across various dimensions',
      icon: { component: 'octicon', name: 'pulse' },
      location: {
        name: RouteNames.Analyze,
        query: {
          section: AnalyticsUICharts.THROUGHPUT,
        },
      },
    },
  });

  ret.push({
    type: 'page',
    label: 'Report: Cumulative flow',
    entity: {
      id: 'report:cumulative-flow',
      description: 'Analyze the flow of work items within boards and cycles',
      icon: { component: 'fluent-icon', name: 'calendar_data_bar' },
      location: {
        name: RouteNames.Analyze,
        query: {
          section: AnalyticsUICharts.CUMULATIVE_FLOW,
        },
      },
    },
  });

  ret.push({
    type: 'page',
    label: 'Report: Burndown',
    entity: {
      id: 'report:burndown',
      description: 'Analyze the rate of work completion over time',
      icon: { component: 'fluent-icon', name: 'arrow_trending_down' },
      location: {
        name: RouteNames.Analyze,
        query: {
          section: AnalyticsUICharts.BURNDOWN,
        },
      },
    },
  });

  ret.push({
    type: 'page',
    label: 'Report: Burnup',
    entity: {
      id: 'report:burnup',
      description: 'Analyze the rate of work completion over time',
      icon: { component: 'fluent-icon', name: 'arrow_trending' },
      location: {
        name: RouteNames.Analyze,
        query: {
          section: AnalyticsUICharts.BURNUP,
        },
      },
    },
  });

  ret.push({
    type: 'page',
    label: 'Report: Churn',
    entity: {
      id: 'report:churn',
      description: 'Analyze the amount of work added and removed over time',
      icon: { component: 'octicon', name: 'diff' },
      location: {
        name: RouteNames.Analyze,
        query: {
          section: AnalyticsUICharts.CHURN,
        },
      },
    },
  });

  ret.push({
    type: 'page',
    label: 'Report: Velocity',
    entity: {
      id: 'report:velocity',
      description: 'Analyze productivity and predictability of work completion',
      icon: { component: 'fluent-icon', name: 'gauge' },
      location: {
        name: RouteNames.Analyze,
        query: {
          section: AnalyticsUICharts.VELOCITY,
        },
      },
    },
  });

  ret.push({
    type: 'page',
    label: 'Report: User Time Tracking',
    entity: {
      id: 'report:user-time-tracking',
      description: 'Analyze the times registered by users when completing work',
      icon: { component: 'custom-icon', name: 'clock' },
      location: {
        name: RouteNames.Analyze,
        query: {
          section: AnalyticsUICharts.USER_TIME_TRACKING,
        },
      },
    },
  });

  // Config

  ret.push({
    type: 'page',
    label: 'Automations',
    entity: {
      id: 'automations',
      description: 'Automate workflows with custom rules',
      icon: { component: 'fluent-icon', name: 'flowchart' },
      location: RouteNames.Automate,
    },
  });

  if (isAdmin) {
    ret.push({
      type: 'page',
      label: 'Settings',
      entity: {
        id: 'settings',
        description: 'Manage organization settings and integrations',
        icon: { component: 'octicon', name: 'gear' },
        location: RouteNames.Organization,
      },
    });

    // Organization

    ret.push({
      type: 'page',
      label: 'Settings: Organization Profile',
      entity: {
        id: 'organization:org-profile',
        description:
          'Organization profile settings including name, logo, timezone and more',
        icon: { component: 'fluent-icon', name: 'organization_16' },
        location: OrganizationPreferencesRoutes.OrganizationProfile,
      },
    });

    ret.push({
      type: 'page',
      label: 'Settings: Users',
      entity: {
        id: 'organization:users',
        description: 'Manage users',
        icon: { component: 'octicon', name: 'person' },
        location: OrganizationPreferencesRoutes.OrganizationUsers,
      },
    });

    ret.push({
      type: 'page',
      label: 'Settings: Teams',
      entity: {
        id: 'organization:teams',
        description: 'Manage teams',
        icon: { component: 'custom-icon', name: 'people' },
        location: OrganizationPreferencesRoutes.OrganizationTeams,
      },
    });

    ret.push({
      type: 'page',
      label: 'Settings: Permissions',
      entity: {
        id: 'organization:permissions',
        description: 'Manage permissions',
        icon: { component: 'octicon', name: 'lock' },
        location: OrganizationPreferencesRoutes.OrganizationPermissions,
      },
    });

    ret.push({
      type: 'page',
      label: 'Settings: Notification Defaults',
      entity: {
        id: 'organization:notifications',
        description: 'Manage organization level notification defaults',
        icon: { component: 'octicon', name: 'bell' },
        location: OrganizationPreferencesRoutes.OrganizationNotifications,
      },
    });

    // Data Model

    enum SettingsSections {
      TYPES = 'types',
      TAGS = 'tags',
      CUSTOM_FIELDS = 'customFields',
      PRIORITY_LEVELS = 'priorityLevels',
      POINT_ESTIMATION = 'pointEstimation',
      TIME_ESTIMATION = 'timeEstimation',
      WORKSPACE_TAB_ORDER = 'workspaceTabOrder',
      LANES = 'lanes',
      CADENCES = 'cadences',
    }

    ret.push({
      type: 'page',
      label: 'Settings: Cadences',
      entity: {
        id: 'organization:cadences',
        description: 'Manage organization level cadence',
        icon: { component: 'fluent-icon', name: 'calendar_sync' },
        location: {
          name: OrganizationPreferencesRoutes.OrganizationSettings,
          query: { section: SettingsSections.CADENCES },
        },
      },
    });

    ret.push({
      type: 'page',
      label: 'Settings: Item Types',
      entity: {
        id: 'organization:item-types',
        description: 'Manage item types',
        icon: { component: 'fluent-icon', name: 'apps' },
        location: {
          name: OrganizationPreferencesRoutes.OrganizationSettings,
          query: { section: SettingsSections.TYPES },
        },
      },
    });

    ret.push({
      type: 'page',
      label: 'Settings: Tags',
      entity: {
        id: 'organization:tags',
        description: 'Manage tags',
        icon: { component: 'octicon', name: 'tag' },
        location: {
          name: OrganizationPreferencesRoutes.OrganizationSettings,
          query: { section: SettingsSections.TAGS },
        },
      },
    });

    ret.push({
      type: 'page',
      label: 'Settings: Custom Fields',
      entity: {
        id: 'organization:custom-fields',
        description: 'Manage custom fields',
        icon: { component: 'custom-icon', name: 'custom-fields' },
        location: {
          name: OrganizationPreferencesRoutes.OrganizationSettings,
          query: { section: SettingsSections.CUSTOM_FIELDS },
        },
      },
    });

    ret.push({
      type: 'page',
      label: 'Settings: Priority Levels',
      entity: {
        id: 'organization:priority-levels',
        description: 'Manage priority levels',
        icon: { component: 'custom-icon', name: 'priority' },
        location: {
          name: OrganizationPreferencesRoutes.OrganizationSettings,
          query: { section: SettingsSections.PRIORITY_LEVELS },
        },
      },
    });

    ret.push({
      type: 'page',
      label: 'Settings: Effort Point Estimation',
      entity: {
        id: 'organization:effort-point-estimation',
        description: 'Manage effort point estimation',
        icon: { component: 'custom-icon', name: 'barbell' },
        location: {
          name: OrganizationPreferencesRoutes.OrganizationSettings,
          query: { section: SettingsSections.POINT_ESTIMATION },
        },
      },
    });

    ret.push({
      type: 'page',
      label: 'Settings: Time Estimation',
      entity: {
        id: 'organization:time-estimation',
        description: 'Manage time estimation',
        icon: { component: 'custom-icon', name: 'clock' },
        location: {
          name: OrganizationPreferencesRoutes.OrganizationSettings,
          query: { section: SettingsSections.TIME_ESTIMATION },
        },
      },
    });

    ret.push({
      type: 'page',
      label: 'Settings: Statuses & WIP Limiters',
      entity: {
        id: 'organization:statuses-wip-limiters',
        description: 'Manage statuses and WIP limiters',
        icon: { component: 'custom-icon', name: 'column-outline' },
        location: {
          name: OrganizationPreferencesRoutes.OrganizationSettings,
          query: { section: SettingsSections.LANES },
        },
      },
    });

    ret.push({
      type: 'page',
      label: 'Settings: Workspace Tab Order',
      entity: {
        id: 'organization:workspace-tab-order',
        description:
          'Manage the order and visibility of tabs in the workspace item drawer',
        icon: { component: 'fluent-icon', name: 'arrow_autofit_width' },
        location: {
          name: OrganizationPreferencesRoutes.OrganizationSettings,
          query: { section: SettingsSections.WORKSPACE_TAB_ORDER },
        },
      },
    });

    // Forms

    enum FormsSections {
      ITEM_REQUESTS = 'item-requests',
    }

    ret.push({
      type: 'page',
      label: 'Settings: Item Request Forms',
      entity: {
        id: 'organization:item-request-forms',
        description: 'Manage item request forms',
        icon: { component: 'fluent-icon', name: 'document_arrow_right' },
        location: {
          name: OrganizationPreferencesRoutes.OrganizationForms,
          query: { section: FormsSections.ITEM_REQUESTS },
        },
      },
    });

    // Integrations

    ret.push({
      type: 'page',
      label: 'Settings: Credentials',
      entity: {
        id: 'organization:credentials',
        description: 'Setup and manage your credentials for integrations',
        icon: { component: 'octicon', name: 'key' },
        location: {
          name: OrganizationPreferencesRoutes.OrganizationIntegrations,
          query: { section: 'credentials' },
        },
      },
    });

    ret.push({
      type: 'page',
      label: 'Settings: GitHub',
      entity: {
        id: 'organization:github',
        description: 'Setup and manage GitHub integration',
        icon: { component: 'octicon', name: 'mark-github' },
        location: {
          name: OrganizationPreferencesRoutes.OrganizationIntegrations,
          query: { section: 'github' },
        },
      },
    });

    ret.push({
      type: 'page',
      label: 'Settings: Slack',
      entity: {
        id: 'organization:slack',
        description: 'Setup and manage Slack integration',
        icon: { component: 'custom-icon', name: 'slack' },
        location: {
          name: OrganizationPreferencesRoutes.OrganizationIntegrations,
          query: { section: 'slack' },
        },
      },
    });

    if (temp_jiraAdoIntegrationsEnabled) {
      ret.push({
        type: 'page',
        label: 'Settings: Jira',
        entity: {
          id: 'organization:jira',
          description: 'Setup and manage Jira integration',
          icon: { component: 'custom-icon', name: 'jira' },
          location: {
            name: OrganizationPreferencesRoutes.OrganizationIntegrations,
            query: { section: 'jira' },
          },
        },
      });

      ret.push({
        type: 'page',
        label: 'Settings: Azure DevOps',
        entity: {
          id: 'organization:ado',
          description: 'Setup and manage Azure DevOps integration',
          icon: { component: 'custom-icon', name: 'ado' },
          location: {
            name: OrganizationPreferencesRoutes.OrganizationIntegrations,
            query: { section: 'ado' },
          },
        },
      });
    }

    // Subscription

    enum SubscriptionSections {
      SUBSCRIPTION_TIER = 'SUBSCRIPTION_TIER',
      PAYMENT_METHOD = 'PAYMENT_METHOD',
      BILLING_DETAILS = 'BILLING_DETAILS',
      BILLING_HISTORY = 'BILLING_HISTORY',
    }

    ret.push({
      type: 'page',
      label: 'Settings: Subscription Tier',
      entity: {
        id: 'organization:subscription-tier',
        description: 'Manage your DevStride subscription',
        icon: { component: 'custom-icon', name: 'custom-subscription' },
        location: {
          name: OrganizationPreferencesRoutes.OrganizationSubscription,
          query: { section: SubscriptionSections.SUBSCRIPTION_TIER },
        },
      },
    });

    ret.push({
      type: 'page',
      label: 'Settings: Billing Details',
      entity: {
        id: 'organization:billing-details',
        description: 'Manage billing details for your organization',
        icon: { component: 'fluent-icon', name: 'credit_card_person' },
        location: {
          name: OrganizationPreferencesRoutes.OrganizationSubscription,
          query: { section: SubscriptionSections.BILLING_DETAILS },
        },
      },
    });

    ret.push({
      type: 'page',
      label: 'Settings: Payment Method',
      entity: {
        id: 'organization:payment-method',
        description: 'Manage payment method for your organization',
        icon: { component: 'octicon', name: 'credit-card' },
        location: {
          name: OrganizationPreferencesRoutes.OrganizationSubscription,
          query: { section: SubscriptionSections.PAYMENT_METHOD },
        },
      },
    });

    ret.push({
      type: 'page',
      label: 'Settings: Billing History',
      entity: {
        id: 'organization:billing-history',
        description: 'View billing history for your organization',
        icon: { component: 'custom-icon', name: 'billing-history' },
        location: {
          name: OrganizationPreferencesRoutes.OrganizationSubscription,
          query: { section: SubscriptionSections.BILLING_HISTORY },
        },
      },
    });

    // DevStride Admin
    if (isDevStrideAdmin) {
      ret.push({
        type: 'page',
        label: 'Settings: Job History',
        entity: {
          id: 'organization:job-history',
          description: 'View job history',
          icon: { component: 'octicon', name: 'history' },
          location: OrganizationPreferencesRoutes.JobHistory,
        },
      });

      ret.push({
        type: 'page',
        label: 'Settings: Products',
        entity: {
          id: 'organization:products',
          description: 'View product offerings by DevStride',
          icon: { component: 'octicon', name: 'credit-card' },
          location: OrganizationPreferencesRoutes.SubscriptionProducts,
        },
      });

      ret.push({
        type: 'page',
        label: 'Settings: All Orgs',
        entity: {
          id: 'organization:all-orgs',
          description:
            'Manage subscriptions for all organizations within DevStride',
          icon: { component: 'octicon', name: 'people' },
          location: OrganizationPreferencesRoutes.OrganizationManagement,
        },
      });
    }
  }

  // User profile

  ret.push({
    type: 'page',
    label: 'My account',
    entity: {
      id: 'my-account',
      description: 'Manage your account settings',
      icon: { component: 'octicon', name: 'person' },
      location: RouteNames.MyAccount,
    },
  });

  ret.push({
    type: 'page',
    label: 'My account: User Profile',
    entity: {
      id: 'user-profile',
      description: 'Manage your user profile settings',
      icon: { component: 'octicon', name: 'person' },
      location: AccountRoutes.AccountProfile,
    },
  });

  ret.push({
    type: 'page',
    label: 'My account: Organizations',
    entity: {
      id: 'organizations',
      description: 'Manage your organizations',
      icon: { component: 'octicon', name: 'organization' },
      location: AccountRoutes.AccountOrganizations,
    },
  });

  ret.push({
    type: 'page',
    label: 'My account: Notification Settings',
    entity: {
      id: 'notification-settings',
      description: 'Manage notification settings',
      icon: { component: 'octicon', name: 'bell' },
      location: AccountRoutes.AccountNotifications,
    },
  });

  ret.push({
    type: 'page',
    label: 'My account: API Keys',
    entity: {
      id: 'api-keys',
      description: 'Manage your API keys',
      icon: { component: 'octicon', name: 'key' },
      location: AccountRoutes.AccountApiKeys,
    },
  });

  // Help

  ret.push({
    type: 'page',
    label: 'Learn',
    entity: {
      id: 'help',
      description: 'Documentation and guides to help you with DevStride',
      icon: { component: 'octicon', name: 'mortar-board' },
      location: 'https://docs.devstride.com',
      isExternal: true,
    },
  });

  return ret;
};

const useMasterSearchRecentsLocalStorageChangeCounter = ref(0);
export function useMasterSearchRecents({
  useAutomaticSyncing = false,
  resultTypes,
}: {
  /** Whether to automatically load and sync results for recents as they are loaded/added/removed */
  useAutomaticSyncing?: boolean;
  /** If specified, only these types will be fetched */
  resultTypes?: RecentMasterSearchResult['resultType'][];
} = {}) {
  const { isAdmin, isDevStrideAdmin } = organizationData();
  const root = useRoot();

  const MAX_RECENTS = 100;
  const LOCAL_STORAGE_KEY = orgScopedKey(
    LocalStorageKeys.MASTER_SEARCH_RECENTS
  );
  const recentsLRU = useLRUArray<RecentMasterSearchResult>({
    max: MAX_RECENTS,
    isEqual: (a, b) => a.type === b.type && a.identifier === b.identifier,
  });

  const setRecentsFromLocalStorage = () => {
    try {
      const storedRecentsStr = localStorage.getItem(LOCAL_STORAGE_KEY);
      const parsedRecents = storedRecentsStr
        ? JSON.parse(storedRecentsStr)
        : [];
      if (Array.isArray(parsedRecents)) {
        recentsLRU.replace(...parsedRecents);
      }
    } catch (e) {
      // ignore, no recents
    }
  };
  setRecentsFromLocalStorage();

  const addRecent = (
    recentType: RecentMasterSearchResult['resultType'],
    identifier: RecentMasterSearchResult['identifier']
  ) => {
    const newRecent: RecentMasterSearchResult = {
      type: 'recent',
      resultType: recentType,
      identifier,
    };
    recentsLRU.push(newRecent);

    // save to localstorage
    localStorage.setItem(
      LOCAL_STORAGE_KEY,
      JSON.stringify(recentsLRU.arrayRecentsLast.value)
    );
    useMasterSearchRecentsLocalStorageChangeCounter.value++;
  };

  const loadedResultsMap = ref(
    new Map<RecentMasterSearchResult['identifier'], MasterSearchResult>()
  );

  const recentsMap = computed<
    Map<RecentMasterSearchResult['identifier'], RecentMasterSearchResult>
  >(() => {
    const ret = new Map<
      RecentMasterSearchResult['identifier'],
      RecentMasterSearchResult
    >(recentsLRU.arrayRecentsFirst.value.map((r) => [r.identifier, r]));
    return ret;
  });

  const getPageRecents = (): PageSearchResult[] => {
    const ret: PageSearchResult[] = [];
    const availablePagesMap = new Map<
      PageSearchResult['entity']['id'],
      PageSearchResult
    >(
      getAvailablePages(
        isAdmin.value,
        isDevStrideAdmin.value,
        root.$store.organization.temp_jiraAdoIntegrationsEnabled
      ).map((p) => [p.entity.id, p])
    );
    recentsLRU.arrayRecentsFirst.value.forEach((recent) => {
      if (recent.resultType !== 'page') return;
      const page = availablePagesMap.get(recent.identifier);
      if (page) ret.push(page);
    });
    return ret;
  };

  const getWorkItemRecents = async (): Promise<WorkItemSearchResult[]> => {
    const allItemNumbersInRecents: WorkItem['number'][] = [];
    const itemNumbersToFetch: WorkItem['number'][] = [];
    recentsLRU.arrayRecentsFirst.value.forEach((recent) => {
      if (recent.resultType === 'work-item') {
        allItemNumbersInRecents.push(recent.identifier);

        if (loadedResultsMap.value.has(recent.identifier))
          return; // already loaded
        else itemNumbersToFetch.push(recent.identifier);
      }
    });

    if (!allItemNumbersInRecents.length) return [];

    const fetchedItems = await (async () => {
      if (!itemNumbersToFetch.length) return [];

      // look in cache first
      const itemsFromCache = itemNumbersToFetch
        .map(
          (number) => root.$store.item.loadFromCache(number) as WorkItem | null
        )
        .filter((i): i is WorkItem => !!i);

      // if everything is found in cache, no need to fetch
      if (itemsFromCache.length === itemNumbersToFetch.length)
        return itemsFromCache;

      // if not, fetch all from server
      return (
        await root.$store.item.searchRaw({
          query: {
            number: itemNumbersToFetch,
            itemType: ItemType.WORKITEM,
            limit: itemNumbersToFetch.length,
          },
        })
      ).items;
    })();

    const ret: WorkItemSearchResult[] = [];
    allItemNumbersInRecents.forEach((itemNumber) => {
      // find in loaded results
      const loadedResult = loadedResultsMap.value.get(itemNumber);
      if (loadedResult) ret.push(loadedResult as WorkItemSearchResult);
      else {
        // find in fetched results
        const item = fetchedItems.find(
          (i: { number: string }) => i.number === itemNumber
        );
        if (item) {
          const r: WorkItemSearchResult = {
            type: 'work-item',
            label: item.title,
            entity: item as WorkItem,
          };
          ret.push(r);
        } else {
          // remove from recents
          const recent = recentsMap.value.get(itemNumber);
          if (recent) recentsLRU.remove(recent);
        }
      }
    });

    return ret;
  };

  const getWorkstreamRecents = async (): Promise<WorkstreamSearchResult[]> => {
    const allFolderNumbersInRecents: Folder['number'][] = [];
    const folderNumbersToFetch: Folder['number'][] = [];
    recentsLRU.arrayRecentsFirst.value.forEach((recent) => {
      if (recent.resultType === 'workstream') {
        allFolderNumbersInRecents.push(recent.identifier);

        if (loadedResultsMap.value.has(recent.identifier))
          return; // already loaded
        else folderNumbersToFetch.push(recent.identifier);
      }
    });

    if (!allFolderNumbersInRecents.length) return [];

    const loadedFolders = await (async () => {
      if (!folderNumbersToFetch.length) return [];

      // look in cache first
      const foldersFromCache = folderNumbersToFetch
        .map(
          (number) => root.$store.item.loadFromCache(number) as Folder | null
        )
        .filter((i): i is Folder => !!i);

      // if everything is found in cache, no need to fetch
      if (foldersFromCache.length === folderNumbersToFetch.length)
        return foldersFromCache;

      // if not, fetch all from server
      return (
        await root.$store.item.searchRaw({
          query: {
            number: folderNumbersToFetch,
            itemType: ItemType.FOLDER,
            limit: folderNumbersToFetch.length,
          },
        })
      ).items;
    })();

    const ret: WorkstreamSearchResult[] = [];
    allFolderNumbersInRecents.forEach((folderNumber) => {
      // find in loaded results
      const loadedResult = loadedResultsMap.value.get(folderNumber);
      if (loadedResult) ret.push(loadedResult as WorkstreamSearchResult);
      else {
        // find in fetched results
        const folder = loadedFolders.find(
          (f: { number: string }) => f.number === folderNumber
        );
        if (folder) {
          const r: WorkstreamSearchResult = {
            type: 'workstream',
            label: folder.title,
            entity: folder as Folder,
          };
          ret.push(r);
        } else {
          // remove from recents
          const recent = recentsMap.value.get(folderNumber);
          if (recent) recentsLRU.remove(recent);
        }
      }
    });

    return ret;
  };

  const getBoardFolderRecents = async (): Promise<
    BoardFolderSearchResult[]
  > => {
    const allFolderIdsInRecents: BoardFolder['id'][] = [];
    const folderIdsToFetch: BoardFolder['id'][] = [];

    recentsLRU.arrayRecentsFirst.value.forEach((recent) => {
      if (recent.resultType === 'board-folder') {
        allFolderIdsInRecents.push(recent.identifier);

        if (loadedResultsMap.value.has(recent.identifier))
          return; // already loaded
        else folderIdsToFetch.push(recent.identifier);
      }
    });

    if (!allFolderIdsInRecents.length) return [];

    const loadedFolders = folderIdsToFetch.length
      ? (await root.$store.board.getBoardFolders({
          ids: folderIdsToFetch,
        })) || []
      : [];

    const ret: BoardFolderSearchResult[] = [];
    allFolderIdsInRecents.forEach((folderId) => {
      // find in loaded results
      const loadedResult = loadedResultsMap.value.get(folderId);
      if (loadedResult) ret.push(loadedResult as BoardFolderSearchResult);
      else {
        // find in fetched results
        const folder = loadedFolders.find(
          (f: { id: string }) => f.id === folderId
        );
        if (folder) {
          const r: BoardFolderSearchResult = {
            type: 'board-folder',
            label: folder.label,
            entity: folder as BoardFolder,
          };
          ret.push(r);
        } else {
          // remove from recents
          const recent = recentsMap.value.get(folderId);
          if (recent) recentsLRU.remove(recent);
        }
      }
    });

    return ret;
  };

  const getBoardRecents = async (): Promise<BoardSearchResult[]> => {
    const allBoardIdsInRecents: Board['id'][] = [];
    const boardIdsToFetch: Board['id'][] = [];
    recentsLRU.arrayRecentsFirst.value.forEach((recent) => {
      if (recent.resultType === 'board') {
        allBoardIdsInRecents.push(recent.identifier);

        if (loadedResultsMap.value.has(recent.identifier))
          return; // already loaded
        else boardIdsToFetch.push(recent.identifier);
      }
    });

    if (!allBoardIdsInRecents.length) return [];

    const loadedBoards = boardIdsToFetch.length
      ? (await root.$store.board.getBoards({
          ids: boardIdsToFetch,
          limit: boardIdsToFetch.length,
        })) || []
      : [];

    const ret: BoardSearchResult[] = [];
    allBoardIdsInRecents.forEach((boardId) => {
      // find in loaded results
      const loadedResult = loadedResultsMap.value.get(boardId);
      if (loadedResult) ret.push(loadedResult as BoardSearchResult);
      else {
        // find in fetched results
        const board = loadedBoards.find(
          (b: { id: string }) => b.id === boardId
        );
        if (board) {
          const r: BoardSearchResult = {
            type: 'board',
            label: board.label,
            entity: board as Board,
          };
          ret.push(r);
        } else {
          // remove from recents
          const recent = recentsMap.value.get(boardId);
          if (recent) recentsLRU.remove(recent);
        }
      }
    });

    return ret;
  };

  const getCycleRecents = async (): Promise<CycleSearchResult[]> => {
    const allCycleIdsInRecents: Board['id'][] = [];
    const cycleIdsToFetch: Board['id'][] = [];
    recentsLRU.arrayRecentsFirst.value.forEach((recent) => {
      if (recent.resultType === 'cycle') {
        allCycleIdsInRecents.push(recent.identifier);

        if (loadedResultsMap.value.has(recent.identifier))
          return; // already loaded
        else cycleIdsToFetch.push(recent.identifier);
      }
    });

    if (!allCycleIdsInRecents.length) return [];

    const loadedCycles = cycleIdsToFetch.length
      ? (await root.$store.board.getBoards({
          ids: cycleIdsToFetch,
          timeBased: true,
          limit: cycleIdsToFetch.length,
        })) || []
      : [];

    const ret: CycleSearchResult[] = [];
    allCycleIdsInRecents.forEach((cycleId) => {
      // find in loaded results
      const loadedResult = loadedResultsMap.value.get(cycleId);
      if (loadedResult) ret.push(loadedResult as CycleSearchResult);
      else {
        // find in fetched results
        const cycle = loadedCycles.find(
          (c: { id: string }) => c.id === cycleId
        );
        if (cycle) {
          const r: CycleSearchResult = {
            type: 'cycle',
            label: cycle.label,
            entity: cycle,
          };
          ret.push(r);
        } else {
          // remove from recents
          const recent = recentsMap.value.get(cycleId);
          if (recent) recentsLRU.remove(recent);
        }
      }
    });

    return ret;
  };

  let roadmapListFetched = false;
  const getRoadmapRecents = async (): Promise<RoadmapSearchResult[]> => {
    const allRoadmapIdsInRecents: Roadmap['id'][] = [];
    const roadmapIdsToFetch: Roadmap['id'][] = [];
    recentsLRU.arrayRecentsFirst.value.forEach((recent) => {
      if (recent.resultType === 'roadmap') {
        allRoadmapIdsInRecents.push(recent.identifier);

        if (loadedResultsMap.value.has(recent.identifier))
          return; // already loaded
        else roadmapIdsToFetch.push(recent.identifier);
      }
    });

    if (!allRoadmapIdsInRecents.length) return [];

    if (root.$store.gantt.ganttRoadmapsList.length) roadmapListFetched = true;

    if (!roadmapListFetched) {
      await root.$store.gantt.fetchGanttRoadmaps();
      roadmapListFetched = true;
    }

    const ret: RoadmapSearchResult[] = [];
    allRoadmapIdsInRecents.forEach((roadmapId) => {
      // find in loaded results
      const loadedResult = loadedResultsMap.value.get(roadmapId);
      if (loadedResult) ret.push(loadedResult as RoadmapSearchResult);
      else {
        // find in fetched results
        const roadmap = root.$store.gantt.ganttRoadmapsList.find(
          (r) => r.id === roadmapId
        );
        if (roadmap) {
          const r: RoadmapSearchResult = {
            type: 'roadmap',
            label: roadmap.name,
            entity: roadmap,
          };
          ret.push(r);
        } else {
          // remove from recents
          const recent = recentsMap.value.get(roadmapId);
          if (recent) recentsLRU.remove(recent);
        }
      }
    });

    return ret;
  };

  const isLoadingRecents = ref(false);
  const getLoadedRecents = async (): Promise<MasterSearchResult[]> => {
    isLoadingRecents.value = true;

    const [
      settledPages,
      settledWorkItems,
      settledWorkstreams,
      settledBoardFolders,
      settledBoards,
      settledCycles,
      settledRoadmaps,
    ] = await Promise.allSettled([
      !resultTypes || resultTypes.includes('page') ? getPageRecents() : [],
      !resultTypes || resultTypes.includes('work-item')
        ? getWorkItemRecents()
        : [],
      !resultTypes || resultTypes.includes('workstream')
        ? getWorkstreamRecents()
        : [],
      !resultTypes || resultTypes.includes('board-folder')
        ? getBoardFolderRecents()
        : [],
      !resultTypes || resultTypes.includes('board') ? getBoardRecents() : [],
      !resultTypes || resultTypes.includes('cycle') ? getCycleRecents() : [],
      !resultTypes || resultTypes.includes('roadmap')
        ? getRoadmapRecents()
        : [],
    ]);
    const pagesMap = new Map<
      PageSearchResult['entity']['id'],
      PageSearchResult
    >(
      (settledPages.status === 'fulfilled' ? settledPages.value : []).map(
        (p) => [p.entity.id, p]
      )
    );
    const workItemsMap = new Map<WorkItem['number'], WorkItemSearchResult>(
      (settledWorkItems.status === 'fulfilled'
        ? settledWorkItems.value
        : []
      ).map((w) => [w.entity.number, w])
    );
    const workstreamsMap = new Map<Folder['number'], WorkstreamSearchResult>(
      (settledWorkstreams.status === 'fulfilled'
        ? settledWorkstreams.value
        : []
      ).map((w) => [w.entity.number, w])
    );
    const boardFoldersMap = new Map<BoardFolder['id'], BoardFolderSearchResult>(
      (settledBoardFolders.status === 'fulfilled'
        ? settledBoardFolders.value
        : []
      ).map((b) => [b.entity.id, b])
    );
    const boardsMap = new Map<Board['id'], BoardSearchResult>(
      (settledBoards.status === 'fulfilled' ? settledBoards.value : []).map(
        (b) => [b.entity.id, b]
      )
    );
    const cyclesMap = new Map<Board['id'], CycleSearchResult>(
      (settledCycles.status === 'fulfilled' ? settledCycles.value : []).map(
        (c) => [c.entity.id, c]
      )
    );
    const roadmapsMap = new Map<Roadmap['id'], RoadmapSearchResult>(
      (settledRoadmaps.status === 'fulfilled' ? settledRoadmaps.value : []).map(
        (r) => [r.entity.id, r]
      )
    );

    const allRecents: MasterSearchResult[] = [];
    recentsLRU.arrayRecentsFirst.value.forEach((recent) => {
      if (recent.resultType === 'page') {
        const page = pagesMap.get(recent.identifier);
        if (page) allRecents.push(page);
      }
      if (recent.resultType === 'work-item') {
        const workItem = workItemsMap.get(recent.identifier);
        if (workItem) allRecents.push(workItem);
      }
      if (recent.resultType === 'workstream') {
        const workstream = workstreamsMap.get(recent.identifier);
        if (workstream) allRecents.push(workstream);
      }
      if (recent.resultType === 'board-folder') {
        const boardFolder = boardFoldersMap.get(recent.identifier);
        if (boardFolder) allRecents.push(boardFolder);
      }
      if (recent.resultType === 'board') {
        const board = boardsMap.get(recent.identifier);
        if (board) allRecents.push(board);
      }
      if (recent.resultType === 'cycle') {
        const cycle = cyclesMap.get(recent.identifier);
        if (cycle) allRecents.push(cycle);
      }
      if (recent.resultType === 'roadmap') {
        const roadmap = roadmapsMap.get(recent.identifier);
        if (roadmap) allRecents.push(roadmap);
      }
    });

    allRecents.forEach((r) => {
      loadedResultsMap.value.set(r.entity.id, r);
      if (r.type === 'work-item' || r.type === 'workstream')
        loadedResultsMap.value.set(r.entity.number, r);
    });

    isLoadingRecents.value = false;

    return allRecents;
  };

  const loadedRecents = computed<MasterSearchResult[]>(() => {
    const ret: MasterSearchResult[] = [];
    recentsLRU.arrayRecentsFirst.value.forEach((recent) => {
      const loadedResult = loadedResultsMap.value.get(recent.identifier);
      if (loadedResult) ret.push(loadedResult);
    });
    return ret;
  });

  if (useAutomaticSyncing) {
    const load = () => {
      setRecentsFromLocalStorage();
      getLoadedRecents();
    };
    window.addEventListener('storage', (e) => {
      if (e.key === LOCAL_STORAGE_KEY) load();
    });
    watch(useMasterSearchRecentsLocalStorageChangeCounter, () => load());
    load();
  }

  return {
    addRecent,
    recents: recentsLRU.arrayRecentsFirst,
    recentsMap,
    isLoadingRecents,
    loadedRecents,
    getLoadedRecents,
  };
}

export function useMasterSearch(opts: {
  searchQuery: Ref<string>;
  debounceDelay: number;
}) {
  const { isAdmin, isDevStrideAdmin, organization } = organizationData();
  const root = useRoot();
  const { navigateToItem, navigateToItemInMap } = useNavigateToItem(root);

  const MAX_RESULTS_PER_TYPE = 5;
  const MAX_RESULTS_SPECIFIC_SEARCH = 50;

  const loadingResults = ref(false);

  const ALL_PAGES = computed<PageSearchResult[]>(() => {
    return getAvailablePages(
      isAdmin.value,
      isDevStrideAdmin.value,
      root.$store.organization.temp_jiraAdoIntegrationsEnabled
    );
  });

  const ALL_PREFIXES: readonly PrefixSearchResult[] = [
    {
      type: 'prefix',
      label: 'Recent',
      entity: {
        id: SearchPrefix.Recent,
        prefix: SearchPrefix.Recent,
        description: `Search recent items (eg: #${SearchPrefix.Recent} My recent item)`,
      },
    },
    {
      type: 'prefix',
      label: 'Page',
      entity: {
        id: SearchPrefix.Page,
        prefix: SearchPrefix.Page,
        description: `Search pages (eg: #${SearchPrefix.Page} My Work)`,
      },
    },
    {
      type: 'prefix',
      label: 'Work item',
      entity: {
        id: SearchPrefix.WorkItem,
        prefix: SearchPrefix.WorkItem,
        description: `Search work items (eg: #${SearchPrefix.WorkItem} Refactor login screen)`,
      },
    },
    {
      type: 'prefix',
      label: 'Workstream',
      entity: {
        id: SearchPrefix.Workstream,
        prefix: SearchPrefix.Workstream,
        description: `Search workstreams (eg: #${SearchPrefix.Workstream} Design Improvements)`,
      },
    },
    {
      type: 'prefix',
      label: 'Board folder',
      entity: {
        id: SearchPrefix.BoardFolder,
        prefix: SearchPrefix.BoardFolder,
        description: `Search board folders (eg: #${SearchPrefix.BoardFolder} Frontend)`,
      },
    },
    {
      type: 'prefix',
      label: 'Board',
      entity: {
        id: SearchPrefix.Board,
        prefix: SearchPrefix.Board,
        description: `Search boards (eg: #${SearchPrefix.Board} Urgent Tasks)`,
      },
    },
    {
      type: 'prefix',
      label: 'Cycle',
      entity: {
        id: SearchPrefix.Cycle,
        prefix: SearchPrefix.Cycle,
        description: `Search cycles (eg: #${SearchPrefix.Cycle} Sprint 1)`,
      },
    },
    {
      type: 'prefix',
      label: 'Gantt roadmap',
      entity: {
        id: SearchPrefix.Roadmap,
        prefix: SearchPrefix.Roadmap,
        description: `Search Gantt roadmaps (eg: #${SearchPrefix.Roadmap} 2022 Q1)`,
      },
    },
  ];

  const bigCache = (() => {
    type CacheKey = `${SearchPrefix | 'all'}::${string}`; // type::cleanSearchStr
    const cache = new Map<CacheKey, MasterSearchResult[]>();
    const alreadyExists = (
      results: MasterSearchResult[],
      result: MasterSearchResult
    ) => {
      if (result.type === 'page')
        return results.some((v) => v.label === result.label);

      return results.some((v) => {
        if (v.type !== result.type) return false;
        return v.entity.id === result.entity.id;
      });
    };
    const trimIfTooBig = () => {
      const sizeInMB = () =>
        JSON.stringify(Array.from(cache.values())).length / 1024 / 1024;
      while (sizeInMB() > 10) {
        cache.delete([...cache.keys()][0]);
      }
    };

    return {
      addResults: (
        q: {
          type: SearchPrefix | 'all';
          cleanSearchStr: string;
        },
        resultOrResults: MasterSearchResult[] | MasterSearchResult
      ) => {
        const key: CacheKey = `${q.type}::${q.cleanSearchStr}`;
        const val = cache.get(key) || [];
        if (Array.isArray(resultOrResults)) {
          resultOrResults.forEach((result) => {
            if (!alreadyExists(val, result)) val.push(result);
          });
        } else {
          if (!alreadyExists(val, resultOrResults)) val.push(resultOrResults);
        }
        cache.set(key, val);

        trimIfTooBig();
      },
      getResults: (q: {
        type: SearchPrefix | 'all';
        cleanSearchStr: string;
      }) => {
        const key: CacheKey = `${q.type}::${q.cleanSearchStr}`;
        return {
          results: cache.get(key) || [],
          hit: cache.has(key),
        };
      },
    };
  })();

  const { isLoadingRecents, loadedRecents, recentsMap, addRecent } =
    useMasterSearchRecents({
      useAutomaticSyncing: true,
    });

  const queryType = computed<SearchPrefix | 'all'>(() => {
    const searchStr = opts.searchQuery.value.trim();
    const isPrefixMatching = (prefix: SearchPrefix) =>
      searchStr === `#${prefix}` || searchStr.startsWith(`#${prefix} `);

    if (isPrefixMatching(SearchPrefix.Recent)) return SearchPrefix.Recent;
    if (isPrefixMatching(SearchPrefix.Page)) return SearchPrefix.Page;
    if (
      isPrefixMatching(SearchPrefix.WorkItem) ||
      /^I[0-9]{1,}$/i.test(searchStr)
    )
      return SearchPrefix.WorkItem;
    if (
      isPrefixMatching(SearchPrefix.Workstream) ||
      /^F[0-9]{1,}$/i.test(searchStr)
    )
      return SearchPrefix.Workstream;
    if (isPrefixMatching(SearchPrefix.BoardFolder))
      return SearchPrefix.BoardFolder;
    if (isPrefixMatching(SearchPrefix.Board)) return SearchPrefix.Board;
    if (isPrefixMatching(SearchPrefix.Cycle)) return SearchPrefix.Cycle;
    if (isPrefixMatching(SearchPrefix.Roadmap)) return SearchPrefix.Roadmap;

    return 'all';
  });

  const getCleanedSearchStr = (searchStr: string) => {
    searchStr = searchStr.trim();
    if (searchStr.startsWith('#')) {
      // is there a second (or more) word after the prefix? if so, use that as the search query, otherwise use empty string
      const isJustPrefix = !searchStr.includes(' ');
      const isValidPrefix = ALL_PREFIXES.some((p) =>
        searchStr.startsWith(`#${p.entity.prefix}`)
      );
      searchStr =
        isJustPrefix || !isValidPrefix
          ? ''
          : searchStr.slice(searchStr.indexOf(' ') + 1); // remove #whatever so '#whatever xyz' becomes 'xyz'
    }
    return searchStr.trim().toLowerCase();
  };

  const isSearchable = computed(() => {
    const query = getCleanedSearchStr(opts.searchQuery.value); // get cleaned query without prefix
    return query.length >= 2;
  });

  const bestMatchSortPredicate = (
    a: MasterSearchResult,
    b: MasterSearchResult
  ) => {
    if (
      recentsMap.value.has(a.entity.id) &&
      !recentsMap.value.has(b.entity.id)
    ) {
      // a is recent, put a first
      return -1;
    } else if (
      !recentsMap.value.has(a.entity.id) &&
      recentsMap.value.has(b.entity.id)
    ) {
      // b is recent, put b first
      return 1;
    }

    const searchStr = getCleanedSearchStr(opts.searchQuery.value);
    // sort by how early the searchStr appears in the label, eg: 'work' > 'network'
    // AND
    // also sort by the length of the label, eg: 'work' > 'workstream'
    const aIndex = a.label.toLowerCase().indexOf(searchStr);
    const bIndex = b.label.toLowerCase().indexOf(searchStr);
    if (aIndex === bIndex) return a.label.length - b.label.length; // shorter label first
    if (aIndex === -1) return 1; // bIndex is not -1, so a is later
    if (bIndex === -1) return -1; // aIndex is not -1, so b is later
    return aIndex - bIndex; // earlier searchStr first
  };

  const queryRecents = async (
    searchStr: string
  ): Promise<MasterSearchResult[]> => {
    searchStr = getCleanedSearchStr(searchStr);
    const recents = loadedRecents.value.filter((r) =>
      r.label.toLowerCase().includes(searchStr)
    );
    // don't clamp recents since they're likely to be very relevant
    return recents.sort(bestMatchSortPredicate);
  };

  const queryPages = async (
    searchStr: string,
    noLimit = false
  ): Promise<PageSearchResult[]> => {
    searchStr = getCleanedSearchStr(searchStr);
    return ALL_PAGES.value
      .filter((page) => {
        return (
          page.label.toLowerCase().includes(searchStr) ||
          page.entity.description.toLowerCase().includes(searchStr)
        );
      })
      .sort(bestMatchSortPredicate)
      .slice(0, noLimit ? Number.MAX_SAFE_INTEGER : MAX_RESULTS_PER_TYPE);
  };

  const loadingWorkItems = ref(false);
  const queryWorkItems = async (
    searchStr: string,
    noLimit = false
  ): Promise<WorkItemSearchResult[]> => {
    searchStr = getCleanedSearchStr(searchStr);
    const isExactSearch = /^I[0-9]{1,}$/i.test(searchStr); // i123 or I123
    const items: WorkItem[] = [];

    loadingWorkItems.value = true;

    if (isExactSearch) {
      const res = await root.$store.item.searchRaw({
        query: {
          itemType: ItemType.WORKITEM,
          number: [searchStr.toUpperCase()],
          limit: 1,
        },
      });
      if (res.items.length) items.push(res.items[0] as WorkItem);
    }

    // exact search failed or not an exact search
    if (!items.length) {
      const searchRes = await root.$store.item.searchRaw({
        query: {
          q: searchStr,
          itemType: ItemType.WORKITEM,
          limit: noLimit ? MAX_RESULTS_SPECIFIC_SEARCH : MAX_RESULTS_PER_TYPE,
        },
      });
      items.push(...(searchRes.items as WorkItem[]));
    }

    loadingWorkItems.value = false;
    return items.map((item) => {
      return {
        type: 'work-item',
        label: item.title,
        entity: item,
      };
    });
  };

  const loadingWorkstreams = ref(false);
  const queryWorkstreams = async (
    searchStr: string,
    noLimit = false
  ): Promise<WorkstreamSearchResult[]> => {
    searchStr = getCleanedSearchStr(searchStr);
    const isExactSearch = /^F[0-9]{1,}$/i.test(searchStr); // // f123 or F123
    const folders: Folder[] = [];

    loadingWorkstreams.value = true;

    if (isExactSearch) {
      const res = await root.$store.item.searchRaw({
        query: {
          itemType: ItemType.FOLDER,
          number: [searchStr.toUpperCase()],
          limit: 1,
        },
      });
      if (res.items.length) folders.push(res.items[0] as Folder);
    }

    // exact search failed or not an exact search
    if (!folders.length) {
      const searchRes = await root.$store.item.searchRaw({
        query: {
          q: searchStr,
          itemType: ItemType.FOLDER,
          limit: noLimit ? MAX_RESULTS_SPECIFIC_SEARCH : MAX_RESULTS_PER_TYPE,
        },
      });
      folders.push(...(searchRes.items as Folder[]));
    }

    loadingWorkstreams.value = false;
    return folders.map((folder) => {
      return {
        type: 'workstream',
        label: folder.title,
        entity: folder,
      };
    });
  };

  const loadingBoardFolders = ref(false);
  const queryBoardFolders = async (
    searchStr: string
  ): Promise<BoardFolderSearchResult[]> => {
    searchStr = getCleanedSearchStr(searchStr);

    loadingBoardFolders.value = true;

    const boardFolders = await root.$store.board.getBoardFolders({
      labelContains: searchStr,
    });

    loadingBoardFolders.value = false;
    return (boardFolders || []).map((bf: BoardFolder) => {
      return {
        type: 'board-folder',
        label: bf.label,
        entity: bf,
      };
    });
  };

  const loadingBoards = ref(false);
  const queryBoards = async (
    searchStr: string,
    noLimit = false
  ): Promise<BoardSearchResult[]> => {
    searchStr = getCleanedSearchStr(searchStr);

    loadingBoards.value = true;

    const boards = await root.$store.board.getBoards({
      labelContains: searchStr,
      timeBased: false,
      limit: noLimit ? MAX_RESULTS_SPECIFIC_SEARCH : MAX_RESULTS_PER_TYPE,
    });

    loadingBoards.value = false;
    return ((boards || []) as Board[]).map((board) => {
      return {
        type: 'board',
        label: board.label,
        entity: board,
      };
    });
  };

  const loadingCycles = ref(false);
  const queryCycles = async (
    searchStr: string,
    noLimit = false
  ): Promise<CycleSearchResult[]> => {
    searchStr = getCleanedSearchStr(searchStr);

    loadingCycles.value = true;

    const cycles = await root.$store.board.getBoards({
      labelContains: searchStr,
      timeBased: true,
      limit: noLimit ? MAX_RESULTS_SPECIFIC_SEARCH : MAX_RESULTS_PER_TYPE,
    });

    loadingCycles.value = false;

    return ((cycles || []) as Board[]).map((cycle) => {
      return {
        type: 'cycle',
        label: cycle.label,
        entity: cycle,
      };
    });
  };

  const loadingRoadmaps = ref(false);
  let roadmapsLoadingOrLoaded = false;
  const queryRoadmaps = async (
    searchStr: string
  ): Promise<RoadmapSearchResult[]> => {
    if (root.$store.gantt.ganttRoadmapsList.length)
      roadmapsLoadingOrLoaded = true;

    if (!roadmapsLoadingOrLoaded) {
      loadingRoadmaps.value = true;
      roadmapsLoadingOrLoaded = true;
      await root.$store.gantt.fetchGanttRoadmaps();
      loadingRoadmaps.value = false;
    }

    searchStr = getCleanedSearchStr(searchStr);
    return root.$store.gantt.ganttRoadmapsList
      .filter((roadmap) => {
        return roadmap.name.toLowerCase().includes(searchStr);
      })
      .map((roadmap): RoadmapSearchResult => {
        return {
          type: 'roadmap',
          label: roadmap.name,
          entity: roadmap,
        };
      })
      .sort(bestMatchSortPredicate);
  };

  const thereWasAnError = ref<string>('');
  const collectedResults = ref<MasterSearchResult[]>([]);
  const queryAll = async (): Promise<void> => {
    thereWasAnError.value = '';
    if (!isSearchable.value) return;

    const searchStr = opts.searchQuery.value.trim();
    const cleanSearchStr = getCleanedSearchStr(searchStr);
    if (!cleanSearchStr) return;

    collectedResults.value = [];

    const promiseCreators: {
      type: SearchPrefix | 'all';
      creator: (
        searchStr: string,
        noLimit?: boolean
      ) => Promise<MasterSearchResult[]>;
    }[] = [];

    if (queryType.value === SearchPrefix.Recent)
      promiseCreators.push({
        type: SearchPrefix.Recent,
        creator: queryRecents,
      });
    if (queryType.value === 'page')
      promiseCreators.push({ type: SearchPrefix.Page, creator: queryPages });
    else if (queryType.value === SearchPrefix.WorkItem)
      promiseCreators.push({
        type: SearchPrefix.WorkItem,
        creator: queryWorkItems,
      });
    else if (queryType.value === SearchPrefix.Workstream)
      promiseCreators.push({
        type: SearchPrefix.Workstream,
        creator: queryWorkstreams,
      });
    else if (queryType.value === SearchPrefix.BoardFolder)
      promiseCreators.push({
        type: SearchPrefix.BoardFolder,
        creator: queryBoardFolders,
      });
    else if (queryType.value === SearchPrefix.Board)
      promiseCreators.push({ type: SearchPrefix.Board, creator: queryBoards });
    else if (queryType.value === SearchPrefix.Cycle)
      promiseCreators.push({ type: SearchPrefix.Cycle, creator: queryCycles });
    else if (queryType.value === SearchPrefix.Roadmap)
      promiseCreators.push({
        type: SearchPrefix.Roadmap,
        creator: queryRoadmaps,
      });
    else {
      promiseCreators.push(
        { type: SearchPrefix.Recent, creator: queryRecents },
        { type: SearchPrefix.Page, creator: queryPages },
        { type: SearchPrefix.WorkItem, creator: queryWorkItems },
        { type: SearchPrefix.Workstream, creator: queryWorkstreams },
        { type: SearchPrefix.BoardFolder, creator: queryBoardFolders },
        { type: SearchPrefix.Board, creator: queryBoards },
        { type: SearchPrefix.Cycle, creator: queryCycles },
        { type: SearchPrefix.Roadmap, creator: queryRoadmaps }
      );
    }

    loadingResults.value = true;

    const pushResults = (newResults: MasterSearchResult[]) => {
      const cacheQuery: {
        type: SearchPrefix | 'all';
        cleanSearchStr: string;
      } = {
        type: queryType.value,
        cleanSearchStr: cleanSearchStr,
      };

      bigCache.addResults(cacheQuery, newResults);

      if (searchStr !== opts.searchQuery.value.trim()) return; // query changed, cache but don't collect

      const nextArr = uniqBy(
        [...collectedResults.value, ...newResults],
        (r) => r.entity.id
      );
      nextArr.sort(bestMatchSortPredicate);
      collectedResults.value = nextArr;
    };

    const promises: Promise<void>[] = [];
    promiseCreators.forEach(async (c) => {
      // try cache first
      const cacheKey: {
        type: SearchPrefix | 'all';
        cleanSearchStr: string;
      } = {
        type: c.type,
        cleanSearchStr,
      };
      const cachedResults = bigCache.getResults(cacheKey);
      if (cachedResults.hit) {
        pushResults(cachedResults.results.filter((r) => r.type === c.type));
        return;
      }

      // cache miss, fetch
      const isSpecificSearch = queryType.value !== 'all';
      const p = c.creator(cleanSearchStr, isSpecificSearch).then(pushResults);
      promises.push(p);
    });

    try {
      await Promise.all(promises);
    } catch (e) {
      thereWasAnError.value =
        get(e, 'response.data.message') ||
        get(e, 'message') ||
        'There was an error while searching';
      console.error('Error while searching', e);
    }

    loadingResults.value = false;
  };
  watch(opts.searchQuery, debounce(queryAll, opts.debounceDelay));

  // TODO: pagination.
  /*
    rough idea:
      - in normal/"all" search, show MAX_RESULTS_PER_TYPE results per type as usual but
        include a more button if there are more results. track this `hasMore` in a ref.
      - when more is clicked, switch to entity search and let existing plumbing perform
        the search normally. this will show MAX_RESULTS_SPECIFIC_SEARCH results per type.
      - show more button in entity search if there are more results. when clicked, this
        more will fetch the next page of results through queryNext() which already knows
        which creator to use since it's a specific search. repeat until no more results.
        don't save these results in cache
        NOTE: track cursors in creators, clear cursors in queryAll().
  */

  const processSearchResult = (result: MasterSearchResult, alt = false) => {
    if (result.type === 'page') {
      const loc = result.entity.location;
      if (result.entity.isExternal) {
        const url = typeof loc === 'string' ? loc : '';
        if (!url) return;
        addRecent('page', result.entity.id); // add to recent because router won't track external links
        window.open(url, '_blank');
        return;
      }

      const name = typeof loc === 'string' ? loc : get(loc, 'name');
      const query = get(loc, 'query', {});
      const params = get(loc, 'params', {});
      root.$router.push({
        name,
        query: query,
        params: {
          ...params,
          organization: organization.value?.name,
        },
      });
    } else if (result.type === 'work-item') {
      if (alt) navigateToItemInMap(result.entity);
      else navigateToItem(result.entity);
    } else if (result.type === 'workstream') {
      if (alt) navigateToItem(result.entity);
      else navigateToItemInMap(result.entity);
    } else if (result.type === 'board-folder') {
      root.$router.push({
        name: RouteNames.BoardFolder,
        params: {
          organization: organization.value?.name,
          folderIdentifier: result.entity.id,
        },
      });
    } else if (result.type === 'board') {
      root.$router.push({
        name: RouteNames.Board,
        params: {
          organization: organization.value?.name,
          boardIdentifier: result.entity.id,
        },
      });
    } else if (result.type === 'cycle') {
      root.$router.push({
        name: RouteNames.Board,
        params: {
          organization: organization.value?.name,
          boardIdentifier: result.entity.id,
        },
      });
    } else if (result.type === 'roadmap') {
      root.$router.push({
        name: RouteNames.Gantt,
        query: {
          ganttId: result.entity.id,
        },
        params: {
          organization: organization.value?.name,
        },
      });
    } else if (result.type === 'prefix') {
      opts.searchQuery.value = `#${result.entity.prefix} `;
    }
  };

  root.$router.afterEach((to) => {
    if (to.query.itemNumber) return; // work item is not a page

    const matchedPages = ALL_PAGES.value.filter((p) => {
      const isSame =
        typeof p.entity.location === 'string'
          ? to.name === p.entity.location
          : (() => {
              const name = get(p.entity.location, 'name');
              const params = get(p.entity.location, 'params', {});
              const query = get(p.entity.location, 'query', {});

              if (to.name !== name) return false;
              if (!Object.entries(params).every(([k, v]) => to.params[k] === v))
                return false;
              if (!Object.entries(query).every(([k, v]) => to.query[k] === v))
                return false;

              return true;
            })();
      return isSame;
    });
    matchedPages.reverse().forEach((p) => {
      addRecent('page', p.entity.id);
    });
  });

  const loadedPageRecents = computed<PageSearchResult[]>(() => {
    return loadedRecents.value.filter(
      (r): r is PageSearchResult => r.type === 'page'
    );
  });
  const loadedWorkItemRecents = computed<WorkItemSearchResult[]>(() => {
    return loadedRecents.value.filter(
      (r): r is WorkItemSearchResult => r.type === 'work-item'
    );
  });
  const loadedWorkstreamRecents = computed<WorkstreamSearchResult[]>(() => {
    return loadedRecents.value.filter(
      (r): r is WorkstreamSearchResult => r.type === 'workstream'
    );
  });
  const loadedBoardFolderRecents = computed<BoardFolderSearchResult[]>(() => {
    return loadedRecents.value.filter(
      (r): r is BoardFolderSearchResult => r.type === 'board-folder'
    );
  });
  const loadedBoardRecents = computed<BoardSearchResult[]>(() => {
    return loadedRecents.value.filter(
      (r): r is BoardSearchResult => r.type === 'board'
    );
  });
  const loadedCycleRecents = computed<CycleSearchResult[]>(() => {
    return loadedRecents.value.filter(
      (r): r is CycleSearchResult => r.type === 'cycle'
    );
  });
  const loadedRoadmapRecents = computed<RoadmapSearchResult[]>(() => {
    return loadedRecents.value.filter(
      (r): r is RoadmapSearchResult => r.type === 'roadmap'
    );
  });

  const collectedSearchedRecentResults = computed<MasterSearchResult[]>(() => {
    return collectedResults.value.filter((r) => {
      if (r.type === 'work-item' || r.type === 'workstream')
        return (
          recentsMap.value.has(r.entity.number) ||
          recentsMap.value.has(r.entity.id)
        );
      return recentsMap.value.has(r.entity.id);
    });
  });
  const collectedPageResults = computed<PageSearchResult[]>(() => {
    return collectedResults.value.filter(
      (r): r is PageSearchResult => r.type === 'page'
    );
  });
  const collectedWorkItemResults = computed<WorkItemSearchResult[]>(() => {
    return collectedResults.value.filter(
      (r): r is WorkItemSearchResult => r.type === 'work-item'
    );
  });
  const collectedWorkstreamResults = computed<WorkstreamSearchResult[]>(() => {
    return collectedResults.value.filter(
      (r): r is WorkstreamSearchResult => r.type === 'workstream'
    );
  });
  const collectedBoardFolderResults = computed<BoardFolderSearchResult[]>(
    () => {
      return collectedResults.value.filter(
        (r): r is BoardFolderSearchResult => r.type === 'board-folder'
      );
    }
  );
  const collectedBoardResults = computed<BoardSearchResult[]>(() => {
    return collectedResults.value.filter(
      (r): r is BoardSearchResult => r.type === 'board'
    );
  });
  const collectedCycleResults = computed<CycleSearchResult[]>(() => {
    return collectedResults.value.filter(
      (r): r is CycleSearchResult => r.type === 'cycle'
    );
  });
  const collectedRoadmapResults = computed<RoadmapSearchResult[]>(() => {
    return collectedResults.value.filter(
      (r): r is RoadmapSearchResult => r.type === 'roadmap'
    );
  });

  return {
    queryType,
    isSearchable,

    ALL_PAGES,
    ALL_PREFIXES,

    isLoadingRecents,
    loadedPageRecents,
    loadedWorkItemRecents,
    loadedWorkstreamRecents,
    loadedBoardFolderRecents,
    loadedBoardRecents,
    loadedCycleRecents,
    loadedRoadmapRecents,
    loadedRecents,

    loadingWorkItems,
    loadingWorkstreams,
    loadingBoardFolders,
    loadingBoards,
    loadingCycles,
    loadingRoadmaps,
    loadingResults,

    collectedSearchedRecentResults,
    collectedPageResults,
    collectedWorkItemResults,
    collectedWorkstreamResults,
    collectedBoardFolderResults,
    collectedBoardResults,
    collectedCycleResults,
    collectedRoadmapResults,
    collectedResults,

    thereWasAnError,

    processSearchResult,
  };
}
